import { useQuery } from '@tanstack/react-query';
import { isEmpty, isNil } from 'lodash';
import {
  FIRM_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE,
  FIRM_INVESTMENTS_BY_MD_QUERY_KEY,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { FirmService } from 'services';
import { throwError } from 'utilities';
import {
  GetFirmInvestmentsParams,
  GetFirmInvestmentsResponse,
  GetFirmInvestmentsReturn,
  UseGetFirmInvestmentsParams,
} from './useGetFirmInvestments.types';

const getFirmInvestments = async (
  params: GetFirmInvestmentsParams
): Promise<GetFirmInvestmentsResponse | null> => {
  const { firmId, fundId, measurementDateSlug, measurementDate, checkForExport } = params;

  const firmService = new FirmService();

  try {
    // Match useGetFundInvestments: omit the date parameter when the slug is
    // empty (Most Recent) so the backend falls back to its latest-per-company
    // logic; otherwise pass the canonical YYYY-MM-DD date.
    const dateParam = isEmpty(measurementDateSlug) ? null : (measurementDate?.date ?? null);
    const firmInvestments = (await firmService.getFirmInvestments(
      firmId,
      fundId ?? null,
      dateParam,
      checkForExport
    )) as GetFirmInvestmentsResponse;

    if (!firmInvestments) {
      return throwError(FIRM_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE);
    }

    return firmInvestments;
  } catch (error) {
    return throwError(FIRM_INVESTMENTS_BY_MD_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFirmInvestments = (params: UseGetFirmInvestmentsParams): GetFirmInvestmentsReturn => {
  const { firmId, fundId, measurementDate, shouldQueryAutomatically = true, checkForExport = false } = params;

  const measurementDateSlug = measurementDate?.slug;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetFirmInvestmentsReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && measurementDate !== undefined && shouldQueryAutomatically,
    queryFn: () =>
      getFirmInvestments({
        firmId: firmId as number,
        fundId,
        measurementDateSlug,
        measurementDate,
        checkForExport,
      }),
    queryKey: [
      FIRM_INVESTMENTS_BY_MD_QUERY_KEY,
      firmId,
      fundId ?? 'all-funds',
      measurementDateSlug ?? 'most-recent',
      measurementDate?.date,
      checkForExport,
    ],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFirmInvestments;
export { getFirmInvestments };
